import {notEmpty} from "../../../utils/common";
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray} from "../../../types-enums/activity";
import {ActivityCarBusState} from "../../../types-enums/activity-car-bus";
import {JourneyDayCardProps} from "../common";
import {isActivityCarBusState} from "../../../utils/check/type-guards";
import {theme} from "../../../styles/theme";

export enum START_OR_END_OF_GROUP {
    start, end
}

/**
 * Filters and retrieves a list of unique days associated with activities belonging to a specified group.
 *
 * This function processes an array of activities, selects only those with a matching `groupId`,
 * extracts their respective `day` property, and ensures that no empty values are included in the result.
 *
 * @param {AllActivityTypesArray} activities - An array of activity objects to filter and process.
 * @param {string | null} groupId - The unique identifier for a group of activities. If null, activities
 *     with a `groupId` of null will be considered.
 * @returns {Array<number>} An array of non-empty `day` values for activities matching the specified `groupId`.
 */
export const getDaysOfActivitiesGroup = (activities: AllActivityTypesArray, groupId: string | null): Array<number> => activities.filter(activity => activity.common.groupId === groupId).map(activity => activity.common.day).filter(notEmpty)

/**
 * Determines if a given activity satisfies the following conditions:
 * 1. It has a defined bus or car route.
 * 2. The count of days is greater than one.
 *
 * @param {ActivityCarBusState} activity - The state object representing activity details,
 * such as route and countDays.
 * @returns {boolean} True if the activity has a defined route and the countDays is greater than one. Otherwise, false.
 */
export const hasCarBusRouteAndCountDaysBiggerOne = (activity: ActivityCarBusState): boolean => Boolean(activity.route && activity.countDays > 1)

/**
 * Retrieves the starting or ending day of a grouped activity based on the specified group ID and type.
 *
 * @param {string} groupId - The unique identifier for the group whose activity days are being evaluated.
 * @param {AllActivityTypesArray} activities - An array containing all activities, each with common properties including group ID and day.
 * @param {START_OR_END_OF_GROUP} startOrEndOfActivity - A constant defining whether to retrieve the start day (minimum) or end day (maximum) of the group.
 * @returns {number} The day corresponding to either the start or end of the grouped activities based on the specified type.
 */
export const getStartOrEndDayOfGroupedActivity = (
    groupId: string,
    activities: AllActivityTypesArray,
    startOrEndOfActivity: START_OR_END_OF_GROUP
): number => {
    const activityDays = activities
        .filter(activity => activity.common.groupId === groupId)
        .map(activity => activity.common.day)
        .filter(Boolean) as number[];
    if (startOrEndOfActivity === START_OR_END_OF_GROUP.start) return Math.min(...activityDays)
    return Math.max(...activityDays)
}

/**
 * Sets the days during a journey to which a specific activity cannot be dragged and dropped.
 *
 * This function calculates the "forbidden days" for an activity based on its type,
 * duration, and its relationship to other grouped activities. The calculated forbidden days
 * are pushed into the provided forbiddenDays array.
 *
 * If a journey has seven days, a grouped Activity has four days, we must exclude certain days to which the Activity
 * cannot be dragged and dropped.
 *
 * Journey duration:  1 ---- 2 ---- 3 ---- 4 ---- 5 ---- 6 ---- 7
 * Activity Duration:        x ---- x ---- x ---- x
 * Day of dragging:                        #
 *
 * @param {AllActivityTypes} activity - The activity for which forbidden days need to be set.
 * This object contains details such as the type, day, and group id.
 * @param {AllActivityTypesArray} activities - The list of all activities in the journey.
 * This is used to identify any grouped activities.
 * @param {JourneyDayCardProps["journeyDurationInDays"]} journeyDurationInDays - The total duration of the journey,
 * represented in days.
 * @param {number[]} forbiddenDays - An array to which the calculated forbidden days will be added.
 */
export const setForbiddenDaysToWhichActivityCannotDraggedAndDropped = (
    activity: AllActivityTypes,
    activities: AllActivityTypesArray,
    journeyDurationInDays: JourneyDayCardProps["journeyDurationInDays"],
    forbiddenDays: number[]
) => {
    const {common: {activityType, groupId}} = activity
    const dayInActivity = activity.common.day
    if (groupId) {
        const beginningDayOfActivity = getStartOrEndDayOfGroupedActivity(groupId, activities, START_OR_END_OF_GROUP.start)
        const endDayOfActivity = getStartOrEndDayOfGroupedActivity(groupId, activities, START_OR_END_OF_GROUP.end)
        if (dayInActivity !== null && typeof journeyDurationInDays !== "undefined") {
            let lastAllowedDay = journeyDurationInDays - (endDayOfActivity - dayInActivity)
            const firstAllowedDay = 1 + (dayInActivity - beginningDayOfActivity)
            switch (activityType) {
                case ACTIVITY_TYPE.car_bus:
                    break;
                case ACTIVITY_TYPE.flight:
                    break;
                case ACTIVITY_TYPE.hotel:
                    lastAllowedDay -= 1
                    break;
            }
            Array.from({length: journeyDurationInDays}, (_, i) => i + 1)
                .filter(journeyDay => journeyDay < firstAllowedDay || journeyDay > lastAllowedDay)
                .forEach(forbiddenDay => forbiddenDays.push(forbiddenDay))

        }
    }
}

/**
 * Checks if the provided activity is related to car or bus transportation and verifies
 * that it has a valid route with a count of days greater than one.
 *
 * The function combines two validation checks:
 * 1. Ensures the activity is in a car or bus state using `isActivityCarBusState`.
 * 2. Confirms the activity has a defined route and that the count of days associated
 *    with it is greater than one using `hasCarBusRouteAndCountDaysBiggerOne`.
 *
 * @param {AllActivityTypes} activity - The activity object to be checked.
 * @returns {boolean} - Returns true if the activity matches the criteria for
 *                      car or bus state with a valid route and count of days
 *                      greater than one; otherwise, returns false.
 */
export const carBusActivityWithRouteAndCountDaysBiggerThanOne = (activity: AllActivityTypes): boolean => Boolean(isActivityCarBusState(activity) && hasCarBusRouteAndCountDaysBiggerOne(activity))


/**
 * Determines if a given activity should be treated as a grouped activity
 * like when the Alt key is not pressed during a move operation. This is specific to
 * certain activity types and includes additional logic for handling
 * car/bus activities with routes and multi-day trips.
 *
 * @param {AllActivityTypes} activity - The activity object to evaluate.
 * Contains a type and potentially other relevant data depending on the
 * activity type.
 * @returns {null | boolean} - Returns `true` if the activity should be
 * treated as a grouped activity; otherwise, returns `null` for unsupported
 * or undefined activity types.
 */
export const treatActivityOnMoveLikeGroupedActivity = (activity: AllActivityTypes): boolean => {
    const {common: {activityType}} = activity
    switch (activityType) {
        case ACTIVITY_TYPE.hotel:
            break;
        case ACTIVITY_TYPE.flight:
            break;
        case ACTIVITY_TYPE.car_bus:
            return carBusActivityWithRouteAndCountDaysBiggerThanOne(activity)
        default:
            activityType satisfies never;
    }
    return false
}

export const COMMON_COLOR = "white"
export const COMMON_THEME_COLOR = theme.colors.white